import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button,
    Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField,
    ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudIcon from '@mui/icons-material/Cloud';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
    useMeteoConfigQuery, useMeteoStatusQuery, useOrpCiselnikQuery,
    useUpdateMeteoConfigMutation,
} from '../../../api/JsdhNotifikaceService/service/MeteoMutations';
import MeteoHydroSection from './MeteoHydroSection';
import { usePohotovostAktivniQuery } from '../../../api/JsdhNotifikaceService/service/PohotovostMutations';
import type {
    MeteoAutomationStatus, MeteoConfigDto, MeteoReaction, OrpEntry,
} from '../../../api/JsdhNotifikaceService/generated/models/Meteo';

const REAKCE_OPTIONS: { value: MeteoReaction; label: string }[] = [
    { value: 'NONE', label: 'Ignorovat' },
    { value: 'NOTIFY', label: 'Upozornit' },
    { value: 'ALARM', label: 'Vyhlásit' },
];

// Per HO-36 mapping §3.4 — typy 7/9/11 BE ignoruje, FE disabled v UI
const AWARENESS_TYPES: { id: number; label: string; disabled?: boolean; reason?: string }[] = [
    { id: 1, label: '1 — Silný vítr → VITR' },
    { id: 2, label: '2 — Sníh a led → SNEHOVA_KALAMITA' },
    { id: 3, label: '3 — Bouřky → BOURKA' },
    { id: 4, label: '4 — Mlha → JINE' },
    { id: 5, label: '5 — Vedra → TEPLOTNI_EXTREM' },
    { id: 6, label: '6 — Mráz → TEPLOTNI_EXTREM' },
    { id: 7, label: '7 — Coastal event', disabled: true, reason: 'BE vždy ignoruje (irelevantní pro ČR)' },
    { id: 8, label: '8 — Nebezpečí požárů → POZARNI_NEBEZPECI' },
    { id: 9, label: '9 — Laviny', disabled: true, reason: 'BE vždy ignoruje (irelevantní pro ČR)' },
    { id: 10, label: '10 — Vydatný déšť → DEST' },
    { id: 11, label: '11 — Výhled', disabled: true, reason: 'Informativní výhled, není ostrá výstraha' },
    { id: 12, label: '12 — Povodňové ohrožení → POVODNE' },
    { id: 13, label: '13 — Povodňová bdělost → POVODNE' },
];

const STATUS_META: Record<MeteoAutomationStatus, { label: string; severity: 'success' | 'info' | 'warning' | 'error'; icon: React.ReactNode }> = {
    RUNNING: { label: 'Automatika běží', severity: 'success', icon: <CheckCircleIcon /> },
    STARTING: { label: 'Inicializace (čekáme na první poll, 1-2 min)', severity: 'info', icon: <HourglassEmptyIcon /> },
    HALTED_DISABLED: { label: 'Automatika vypnuta (admin)', severity: 'info', icon: <CloudIcon /> },
    HALTED_NO_POLL: { label: 'Čeká — ČHMÚ neprovedl žádný úspěšný poll', severity: 'warning', icon: <WarningAmberIcon /> },
    HALTED_STALE: { label: 'Pozastaveno — ČHMÚ data zastaralá', severity: 'warning', icon: <WarningAmberIcon /> },
    HALTED_UNAVAILABLE: { label: 'Pozastaveno — chmu-service nedostupná', severity: 'error', icon: <WarningAmberIcon /> },
};

const MeteoTab: React.FC = () => {
    const orpQuery = useOrpCiselnikQuery();
    const configQuery = useMeteoConfigQuery();
    const statusQuery = useMeteoStatusQuery();
    const aktivniPohotovosti = usePohotovostAktivniQuery();
    const updateMutation = useUpdateMeteoConfigMutation();

    // Form state
    const [enabled, setEnabled] = useState(false);
    const [selectedOrp, setSelectedOrp] = useState<OrpEntry[]>([]);
    const [reakceZluta, setReakceZluta] = useState<MeteoReaction>('NONE');
    const [reakceOranzova, setReakceOranzova] = useState<MeteoReaction>('NOTIFY');
    const [reakceCervena, setReakceCervena] = useState<MeteoReaction>('ALARM');
    const [reakcePerTyp, setReakcePerTyp] = useState<Record<number, MeteoReaction>>({});
    const [maxStalenessMinutes, setMaxStalenessMinutes] = useState(60);
    const [dirty, setDirty] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    // Initialize form from server config
    useEffect(() => {
        if (!configQuery.data || !orpQuery.data) return;
        const cfg = configQuery.data;
        setEnabled(cfg.enabled);
        const orpMap = new Map(orpQuery.data.map(o => [o.cisorp, o]));
        setSelectedOrp((cfg.cisorps || []).map(c => orpMap.get(c) ?? { cisorp: c, nazev: c }));
        setReakceZluta(cfg.reakceZluta);
        setReakceOranzova(cfg.reakceOranzova);
        setReakceCervena(cfg.reakceCervena);
        setReakcePerTyp(cfg.reakcePerTyp ?? {});
        setMaxStalenessMinutes(cfg.maxStalenessMinutes);
        setDirty(false);
    }, [configQuery.data, orpQuery.data]);

    const markDirty = () => setDirty(true);

    // Confirm dialog content — compute diff vs server state
    const diff = useMemo(() => {
        const oldCisorps = new Set(configQuery.data?.cisorps ?? []);
        const newCisorps = new Set(selectedOrp.map(o => o.cisorp));
        const removed: string[] = [];
        const added: string[] = [];
        oldCisorps.forEach(c => { if (!newCisorps.has(c)) removed.push(c); });
        newCisorps.forEach(c => { if (!oldCisorps.has(c)) added.push(c); });
        const wasEnabled = configQuery.data?.enabled ?? false;
        const willDisable = wasEnabled && !enabled;
        // Count active AUTO pohotovosti per removed cisorp (FE client-side filter, žádný BE endpoint)
        const allAktivni = aktivniPohotovosti.data ?? [];
        const removedCounts = removed.map(c => ({
            cisorp: c,
            count: allAktivni.filter(p => p.source === 'AUTO_METEO' && p.cisorp === c).length,
        }));
        const totalAutoActive = allAktivni.filter(p => p.source === 'AUTO_METEO').length;
        return { added, removed, removedCounts, willDisable, totalAutoActive, wasEnabled };
    }, [configQuery.data, selectedOrp, enabled, aktivniPohotovosti.data]);

    const handleSaveClick = () => {
        // Confirm dialog jen pokud potenciální data loss (disable nebo odebrání cisorp s aktivní AUTO)
        const needsConfirm = diff.willDisable
            || diff.removedCounts.some(r => r.count > 0);
        if (needsConfirm) {
            setConfirmOpen(true);
        } else {
            doSave();
        }
    };

    const doSave = () => {
        const dto: MeteoConfigDto = {
            enabled,
            cisorps: selectedOrp.map(o => o.cisorp),
            reakceZluta,
            reakceOranzova,
            reakceCervena,
            reakcePerTyp,
            maxStalenessMinutes,
        };
        updateMutation.mutate(dto, {
            onSuccess: () => {
                setConfirmOpen(false);
                setDirty(false);
            },
        });
    };

    const setPerTyp = (typ: number, value: MeteoReaction | '') => {
        setReakcePerTyp(prev => {
            const next = { ...prev };
            if (value === '') delete next[typ];
            else next[typ] = value;
            return next;
        });
        markDirty();
    };

    if (configQuery.isLoading || orpQuery.isLoading) {
        return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;
    }

    // Bug-inspector 2026-06-09: Save tlačítko musí čekat na aktivniPohotovosti,
    // jinak confirm dialog s počty AUTO může propsat false negative při loading.
    const saveBlockedByLoadingCounts = aktivniPohotovosti.isLoading;

    const tooManyOrp = selectedOrp.length > 10;
    const status = statusQuery.data?.automationStatus;
    const statusMeta = status ? STATUS_META[status] : null;

    return (
        <Paper sx={{ p: 3 }}>
            <Stack spacing={3}>
                {/* Health banner */}
                {statusMeta && (
                    <Alert severity={statusMeta.severity} icon={statusMeta.icon}>
                        <strong>{statusMeta.label}</strong>
                        {statusQuery.data && (
                            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                                Poslední úspěšný fetch: {statusQuery.data.lastFetchSuccessAt
                                    ? new Date(statusQuery.data.lastFetchSuccessAt).toLocaleString('cs-CZ')
                                    : 'nikdy'}
                                {statusQuery.data.activeWarnings > 0
                                    && ` • aktivní výstrahy: ${statusQuery.data.activeWarnings}`}
                                {statusQuery.data.activeAutoPohotovostCount > 0
                                    && ` • AUTO pohotovosti: ${statusQuery.data.activeAutoPohotovostCount}`}
                            </Typography>
                        )}
                        {statusQuery.data?.errors && statusQuery.data.errors.length > 0 && (
                            <Typography variant="caption" component="div" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                {statusQuery.data.errors.join(', ')}
                            </Typography>
                        )}
                    </Alert>
                )}

                {/* Header */}
                <Typography variant="h6">
                    <CloudIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Meteo automatické pohotovosti z ČHMÚ
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Konfigurace automatického vyhlašování pohotovostí na základě meteorologických
                    výstrah ČHMÚ. Default = vše vypnuto (opt-in).
                </Typography>

                {/* Enable toggle */}
                <ToggleButtonGroup
                    exclusive
                    value={enabled ? 'on' : 'off'}
                    onChange={(_, v) => { if (v !== null) { setEnabled(v === 'on'); markDirty(); } }}
                >
                    <ToggleButton value="off">Vypnuto</ToggleButton>
                    <ToggleButton value="on" color="primary">Zapnuto</ToggleButton>
                </ToggleButtonGroup>

                {/* Multi-ORP autocomplete */}
                <FormControl fullWidth>
                    <Autocomplete
                        multiple
                        options={orpQuery.data ?? []}
                        getOptionLabel={(o) => `${o.cisorp} — ${o.nazev}`}
                        isOptionEqualToValue={(o, v) => o.cisorp === v.cisorp}
                        value={selectedOrp}
                        onChange={(_, v) => { setSelectedOrp(v); markDirty(); }}
                        renderTags={(value, getTagProps) =>
                            value.map((opt, idx) => {
                                const { key, ...props } = getTagProps({ index: idx });
                                return <Chip key={key} {...props} label={`${opt.cisorp} — ${opt.nazev}`} />;
                            })
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Sledované ORP (obvody obcí s rozšířenou působností)"
                                placeholder="Vybrat ORP…" />
                        )}
                    />
                    {tooManyOrp && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            Vybráno {selectedOrp.length} ORP — velký počet může znamenat mnoho
                            notifikací během bouřkové sezóny.
                        </Alert>
                    )}
                </FormControl>

                {/* Per-stupen baseline */}
                <Typography variant="subtitle1">Reakce podle stupně výstrahy</Typography>
                <Tooltip title="Doporučení: žlutá=Ignorovat, oranžová=Upozornit, červená=Vyhlásit pohotovost">
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <SegmentedReaction label="🟡 Žlutá" value={reakceZluta}
                            onChange={(v) => { setReakceZluta(v); markDirty(); }} />
                        <SegmentedReaction label="🟠 Oranžová" value={reakceOranzova}
                            onChange={(v) => { setReakceOranzova(v); markDirty(); }} />
                        <SegmentedReaction label="🔴 Červená" value={reakceCervena}
                            onChange={(v) => { setReakceCervena(v); markDirty(); }} />
                    </Stack>
                </Tooltip>

                {/* Per-typ override accordion */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Pokročilé — reakce per typ výstrahy (override)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Override má prioritu nad baseline. Prázdné = použít baseline dle stupně.
                            Typy 7, 9, 11 BE vždy ignoruje (irelevantní pro ČR nebo informativní výhled).
                        </Typography>
                        <Stack spacing={1}>
                            {AWARENESS_TYPES.map(t => (
                                <Stack key={t.id} direction="row" alignItems="center" spacing={2}>
                                    <Tooltip title={t.disabled ? (t.reason ?? '') : ''}>
                                        <Typography sx={{ minWidth: 240, opacity: t.disabled ? 0.4 : 1 }}>
                                            {t.label}
                                        </Typography>
                                    </Tooltip>
                                    <FormControl size="small" sx={{ minWidth: 180 }} disabled={t.disabled}>
                                        <InputLabel>Override</InputLabel>
                                        <Select
                                            label="Override"
                                            value={reakcePerTyp[t.id] ?? ''}
                                            onChange={(e) => setPerTyp(t.id, (e.target.value as MeteoReaction | ''))}
                                        >
                                            <MenuItem value=""><em>(použít baseline)</em></MenuItem>
                                            {REAKCE_OPTIONS.map(o => (
                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            ))}
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* HO-94 E4 — Hydro per-stanice SPA varování (samostatná sekce, vlastní save flow) */}
                <MeteoHydroSection />

                {/* Max staleness */}
                <TextField
                    label="Max prodleva ČHMÚ feedu (minuty)"
                    type="number"
                    value={maxStalenessMinutes}
                    onChange={(e) => { setMaxStalenessMinutes(Number(e.target.value)); markDirty(); }}
                    inputProps={{ min: 5, max: 1440 }}
                    helperText="Pokud chmu-service nevrátí čerstvé výstrahy do této doby, systém zastaví automatické vyhlašování a upozorní admin."
                    sx={{ maxWidth: 400 }}
                />

                {/* Save bar */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        variant="contained"
                        disabled={!dirty || updateMutation.isPending
                            || (enabled && selectedOrp.length === 0)
                            || saveBlockedByLoadingCounts}
                        onClick={handleSaveClick}
                    >
                        {updateMutation.isPending ? 'Ukládám…'
                            : saveBlockedByLoadingCounts ? 'Načítám…'
                            : 'Uložit'}
                    </Button>
                </Box>
                {enabled && selectedOrp.length === 0 && (
                    <Alert severity="warning">Vyberte alespoň jedno ORP, jinak automatika nepoběží.</Alert>
                )}
            </Stack>

            {/* Confirm dialog — disable nebo odebrání cisorp s aktivními AUTO */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Potvrdit změny konfigurace</DialogTitle>
                <DialogContent>
                    {diff.willDisable ? (
                        <DialogContentText>
                            Vypínáte celou meteo automatiku. Bude uzavřeno
                            <strong> {diff.totalAutoActive} aktivních AUTO pohotovostí</strong>.
                        </DialogContentText>
                    ) : (
                        <DialogContentText>
                            Odeberete tyto ORP, čímž se uzavřou jejich aktivní automatické pohotovosti:
                        </DialogContentText>
                    )}
                    {!diff.willDisable && diff.removedCounts.length > 0 && (
                        <Box component="ul" sx={{ mt: 1 }}>
                            {diff.removedCounts.map(r => (
                                <li key={r.cisorp}>
                                    ORP {r.cisorp}: <strong>{r.count}</strong> AUTO pohotovost{r.count === 1 ? '' : 'i'}
                                </li>
                            ))}
                        </Box>
                    )}
                    {diff.added.length > 0 && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Přidané ORP: {diff.added.join(', ')}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Zrušit</Button>
                    <Button variant="contained" color="warning" onClick={doSave} disabled={updateMutation.isPending}>
                        Pokračovat
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

const SegmentedReaction: React.FC<{
    label: string;
    value: MeteoReaction;
    onChange: (v: MeteoReaction) => void;
}> = ({ label, value, onChange }) => (
    <Box>
        <Typography variant="body2" sx={{ mb: 0.5 }}>{label}</Typography>
        <ToggleButtonGroup exclusive value={value} onChange={(_, v) => { if (v !== null) onChange(v); }}>
            {REAKCE_OPTIONS.map(o => (
                <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
            ))}
        </ToggleButtonGroup>
    </Box>
);

export default MeteoTab;
